import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { FileDownload, Print } from "@mui/icons-material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import { log } from "node:console";

export enum BATCH_STATUS {
  SEALED = "SEALED",
  OPENED = "OPENED",
  EXPIRED = "EXPIRED",
  CONSUMED = "CONSUMED",
  DISCARDED = "DISCARDED",
}

const AssetBatch: React.FC<any> = () => {
  const [editData, setEditData] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [collectionCenters, setCollectionCenters] = useState<any[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedAssetFilter, setSelectedAssetFilter] = useState<string>("");
  const [selectedCenterFilter, setSelectedCenterFilter] = useState<string>("");

  const [formData, setFormData] = useState({
    assetId: "",
    batchNo: "",
    collectionCenterId: "",
    totalQuantity: "",
    testPerItem: "",
    expiryDate: "",
    openedDate: "",
    status: "SEALED",
  });

  const [errors, setErrors] = useState({
    assetId: false,
    batchNo: false,
    collectionCenterId: false,
    totalQuantity: false,
    testPerItem: false,
    expiryDate: false,
    status: false,
  });

  const [assetBatchData, setAssetBatchData] = useState<any>();

  const fetchAssets = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/assets/getAllAssets`,
        {
          params: {
            count: 1000,
            pageNo: 1,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data.isSuccess) {
        setAssets(res.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCollectionCenters = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/collection-center/getAllBySearch`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data?.isSuccess) {
        setCollectionCenters(res.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssetBatch = async () => {
    try {
      setLoading(true);
      const params: any = {
        count: rowsPerPage,
        pageNo: page + 1,
      };
      
      if (selectedAssetFilter) {
        params.assetId = selectedAssetFilter;
      }
      
      if (selectedCenterFilter) {
        params.collectionCenterId = selectedCenterFilter;
      }
      
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/asset-batch/findAllAssetBatch`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (res?.data.isSuccess) {
        setAssetBatchData(res.data?.data);
        setTotal(res.data?.totalCount);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching asset batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchCollectionCenters();
  }, []);

  useEffect(() => {
    fetchAssetBatch();
  }, [page, rowsPerPage, selectedAssetFilter, selectedCenterFilter]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "totalQuantity" || name === "testPerItem" ? value.replace(/^0+/, "") : value,
    }));
    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors = {
      assetId: !formData.assetId,
      batchNo: !formData.batchNo,
      collectionCenterId: !formData.collectionCenterId,
      totalQuantity: !formData.totalQuantity || Number(formData.totalQuantity) < 1,
      testPerItem: !formData.testPerItem || Number(formData.testPerItem) < 1,
      expiryDate: !formData.expiryDate,
      status: !formData.status,
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error)) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    const payload: any = {
      assetId: formData.assetId,
      batchNo: formData.batchNo,
      collectionCenterId: formData.collectionCenterId,
      totalQuantity: Number(formData.totalQuantity),
      testPerItem: Number(formData.testPerItem),
      expiryDate: formData.expiryDate,
      status: formData.status,
    };
    
    // Only include openedDate if it has a value
    if (formData.openedDate && formData.openedDate !== "") {
      payload.openedDate = formData.openedDate;
    }
    
    try {
      if (editData) {
       const res= await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/asset-batch/updateById/${editData}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        toast.success("Asset batch updated successfully");
      } else {
      const res=  await axios.post(
          `${process.env.REACT_APP_LAB_URL}/asset-batch/addAssetBatch`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log(res,"wgVB");
        
        toast.success("Asset batch added successfully");
      }
      fetchAssetBatch();
      setEditData(null);
      setFormData({
        assetId: "",
        batchNo: "",
        collectionCenterId: "",
        totalQuantity: "",
        testPerItem: "",
        expiryDate: "",
        openedDate: "",
        status: "SEALED",
      });
      setErrors({
        assetId: false,
        batchNo: false,
        collectionCenterId: false,
        totalQuantity: false,
        testPerItem: false,
        expiryDate: false,
        status: false,
      });
    } catch (error: any) {
      toast.error(error.message);
      toast.error(error.response?.data?.message || "Error submitting form");
    }
  };

  const handleEditClick = (item: any) => {
    setEditData(item._id);
    setFormData({
      assetId: item?.assetId || "",
      batchNo: item.batchNo || "",
      collectionCenterId: item?.collectionCenterId || "",
      totalQuantity: item.totalQuantity || "",
      testPerItem: item.testPerItem || "",
      expiryDate: item.expiryDate ? item.expiryDate.split("T")[0] : "",
      openedDate: item.openedDate ? item.openedDate.split("T")[0] : "",
      status: item.status || "SEALED",
    });
    setErrors({
      assetId: false,
      batchNo: false,
      collectionCenterId: false,
      totalQuantity: false,
      testPerItem: false,
      expiryDate: false,
      status: false,
    });
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/asset-batch/deleteById/${selectedItemId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response?.data.isSuccess) {
        toast.success(response?.data.message);
      }
      fetchAssetBatch();
      setEditData(null);
      setFormData({
        assetId: "",
        batchNo: "",
        collectionCenterId: "",
        totalQuantity: "",
        testPerItem: "",
        expiryDate: "",
        openedDate: "",
        status: "SEALED",
      });
      setErrors({
        assetId: false,
        batchNo: false,
        collectionCenterId: false,
        totalQuantity: false,
        testPerItem: false,
        expiryDate: false,
        status: false,
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!assetBatchData?.length) return toast.warn("No data to export.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Header
    doc
      .setFontSize(14)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("ASSET BATCH REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Metadata
    yPosition += 5;
    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 8;

    const head = [
      ["SN", "Asset Name", "Batch No", "Collection Center", "Total Qty", "Rem Qty", "Test/Item", "Expiry Date", "Status"]
    ];

    const body = assetBatchData.map((item: any, index: number) => [
      index + 1,
      item.assetDetails?.assetName || "",
      item.batchNo || "",
      item.collectionCenterDetails?.collectionCenterName || "",
      item.totalQuantity || "",
      item.remainingQuantity || "",
      item.testPerItem || "",
      item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : "",
      item.status || "",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 3
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [40, 40, 40],
        cellPadding: 2,
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didDrawPage: function(data) {
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(6);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.output("dataurlnewwindow");
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!assetBatchData?.length) return toast.warn("No data to export.");

    const worksheetData = assetBatchData.map((item: any, index: number) => ({
      SN: index + 1,
      "Asset Name": item.assetDetails?.assetName || "",
      "Batch No": item.batchNo || "",
      "Collection Center": item.collectionCenterDetails?.collectionCenterName || "",
      "Total Quantity": item.totalQuantity || "",
      "Remaining Quantity": item.remainingQuantity || "",
      "Test Per Item": item.testPerItem || "",
      "Expiry Date": item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : "",
      "Opened Date": item.openedDate ? new Date(item.openedDate).toLocaleDateString() : "",
      "Status": item.status || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Asset Batch");
    XLSX.writeFile(workbook, "AssetBatch.xlsx");
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          mb: 1,
          gap: { xs: 2, md: 0 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Asset Batch
        </Typography>
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center", 
            gap: 1,
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload sx={{ display: { xs: "none", sm: "block" } }} />}
              onClick={exportToExcel}
              sx={{
                flex: { xs: 1, sm: "none" },
                minWidth: { xs: "auto", md: "80px" },
                fontSize: { xs: "12px", md: "12px" },
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Excel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Print sx={{ display: { xs: "none", sm: "block" } }} />}
              onClick={exportToPDF}
              sx={{
                flex: { xs: 1, sm: "none" },
                minWidth: { xs: "auto", md: "80px" },
                fontSize: { xs: "12px", md: "12px" },
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Print
            </Button>
          </Box>
          <Autocomplete
            size="small"
            options={collectionCenters}
            getOptionLabel={(option) => option.collectionCenterName}
            value={
              collectionCenters.find((cc) => cc._id === selectedCenterFilter) ?? null
            }
            onChange={(_, newValue) => {
              setSelectedCenterFilter(newValue?._id ?? "");
              setPage(0);
            }}
            sx={{ 
              width: { xs: "100%", sm: 200, md: 200 },
              minWidth: { sm: 150 }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Collection Center"
                className="custom-input"
                placeholder="Filter by center..."
              />
            )}
          />
          <Autocomplete
            size="small"
            options={assets}
            getOptionLabel={(option) => option.assetName}
            value={
              assets.find((asset) => asset._id === selectedAssetFilter) ?? null
            }
            onChange={(_, newValue) => {
              setSelectedAssetFilter(newValue?._id ?? "");
              setPage(0);
            }}
            sx={{ 
              width: { xs: "100%", sm: 200, md: 200 },
              minWidth: { sm: 150 }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset"
                className="custom-input"
                placeholder="Filter by asset..."
              />
            )}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Asset Batch" : "Add New Asset Batch"}
            </Typography>

            <Autocomplete
              size="small"
              options={assets}
              getOptionLabel={(option) => option.assetName}
              value={
                assets.find((asset) => asset._id === formData.assetId) ?? null
              }
              onChange={(_, newValue) => {
                handleChange({
                  target: {
                    name: "assetId",
                    value: newValue?._id ?? "",
                  },
                } as any);
                if (errors.assetId) {
                  setErrors((prev) => ({ ...prev, assetId: false }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset"
                  className="custom-input"
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Select asset..."
                  required
                  error={errors.assetId}
                />
              )}
            />

            <TextField
              label="Batch No"
              name="batchNo"
              value={formData.batchNo}
              fullWidth
              size="small"
              onChange={handleChange}
              className="custom-input"
              required
              error={errors.batchNo}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              size="small"
              options={collectionCenters}
              getOptionLabel={(option) => option.collectionCenterName}
              value={
                collectionCenters.find((cc) => cc._id === formData.collectionCenterId) ?? null
              }
              onChange={(_, newValue) => {
                handleChange({
                  target: {
                    name: "collectionCenterId",
                    value: newValue?._id ?? "",
                  },
                } as any);
                if (errors.collectionCenterId) {
                  setErrors((prev) => ({ ...prev, collectionCenterId: false }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Collection Center"
                  className="custom-input"
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="Select collection center..."
                  required
                  error={errors.collectionCenterId}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Total Quantity"
                name="totalQuantity"
                className="custom-input"
                type="number"
                value={formData.totalQuantity}
                size="small"
                onChange={handleChange}
                required
                error={errors.totalQuantity}
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Test Per Item"
                name="testPerItem"
                className="custom-input"
                type="number"
                value={formData.testPerItem}
                size="small"
                onChange={handleChange}
                required
                error={errors.testPerItem}
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Expiry Date"
                name="expiryDate"
                className="custom-input"
                type="date"
                value={formData.expiryDate}
                size="small"
                onChange={handleChange}
                required
                error={errors.expiryDate}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Opened Date"
                name="openedDate"
                className="custom-input"
                type="date"
                value={formData.openedDate}
                size="small"
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Autocomplete
              size="small"
              options={Object.values(BATCH_STATUS)}
              value={formData.status ?? null}
              onChange={(_, value) => {
                handleChange({
                  target: { name: "status", value: value ?? "" },
                } as any);
                if (errors.status) {
                  setErrors((prev) => ({ ...prev, status: false }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  className="custom-input"
                  fullWidth
                  required
                  error={errors.status}
                  sx={{ mb: 2 }}
                  placeholder="Select status..."
                />
              )}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
            >
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8}>
          <DynamicTable
            headers={[
              "SN",
              "Asset Name",
              "Batch No",
              "Collection Center",
              "Total Qty",
              "Rem Qty",
              "Test/Item",
              "Expiry Date",
              "Status",
              "Actions",
            ]}
            data={assetBatchData?.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item.assetDetails?.assetName || "",
              item.batchNo || "",
              item.collectionCenterDetails?.collectionCenterName || "",
              item.totalQuantity || "",
              item.remainingQuantity || "",
              item.testPerItem || "",
              item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : "",
              item.status || "",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(_e, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(assetBatchData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(assetBatchData[rowIndex]?._id),
              },
            ]}
            height={"60vh"}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default AssetBatch;
